/**
 * ONE CONTRACT, TWO CONSUMERS. The api validates requests and shapes
 * responses with these schemas; the web app parses responses with the same
 * ones. There is no hand-written mirror to drift.
 */
import { z } from "zod";
export { Uuid, IsoDate, Currency } from "./primitives.js";
// The closed lists more than one contract names. A leaf, for the same reason.
export * from "./vocabulary.js";
export * from "./workWords.js";
export * from "./chatWords.js";
import { Uuid, IsoDate } from "./primitives.js";
// ── envelope ────────────────────────────────────────────────────────────────
/**
 * WHICH FIELD WAS WRONG.
 *
 * A refusal that says only "The request is not valid" is a refusal a person
 * cannot act on: they have to guess which of four fields the server meant.
 * Every validation failure now names its field and carries the server's own
 * sentence for it, and the screen shows it against the field itself.
 */
export const FieldError = z.object({ field: z.string(), message: z.string() });
export const ErrorEnvelope = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
        fields: z.array(FieldError).optional(),
        requestId: z.string().optional(),
    }),
});
// ── health ──────────────────────────────────────────────────────────────────
export const DependencyCheck = z.enum(["ok", "fail", "skipped"]);
export const HealthChecks = z.object({
    database: DependencyCheck,
    redis: DependencyCheck,
    objectStore: DependencyCheck,
});
export const HealthResponse = z.object({
    status: z.enum(["ok", "degraded"]),
    version: z.string(),
    uptimeSeconds: z.number().int().nonnegative(),
    checks: HealthChecks,
    runtime: z.object({ databaseRole: z.string(), sessionStore: z.enum(["redis", "memory"]), executionMode: z.enum(["request", "worker"]) }),
});
/** DEP-004: readiness is semantic. 200 only when every REQUIRED dependency answers; otherwise 503 with the same body. */
export const ReadyResponse = z.object({
    ready: z.boolean(),
    status: z.enum(["ok", "degraded"]),
    checks: HealthChecks,
    required: z.array(z.enum(["database", "redis", "objectStore"])),
});
// ── identity ────────────────────────────────────────────────────────────────
export const Email = z.string().trim().toLowerCase().email().max(254);
/**
 * THE PASSWORD RULE, STATED ONCE.
 *
 * It lives here, in the contract both sides read, for the same reason every
 * other rule does: a copy in the browser would be a second rule to disagree
 * with the server's. What changed in R5.1 is that the rule is now SHOWN
 * before a person can break it, and its refusal names the field — a first
 * attempt at opening a firm should not come back "The request is not valid"
 * with no way to tell which of four fields was wrong.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_RULES = Object.freeze([
    `At least ${PASSWORD_MIN_LENGTH} characters.`,
    "Anything you can type — a phrase you will remember is stronger than a short, complicated word.",
]);
export const Password = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
    .max(1024, "That password is too long.");
export const MemberRole = z.enum(["PARTNER", "MANAGER", "STAFF"]);
export const RegisterFirmRequest = z.object({
    firmName: z.string().trim().min(2).max(200),
    email: Email,
    password: Password,
    displayName: z.string().trim().min(1).max(200),
    /** Private beta (migration 0029): the invitation code an operator issued. Required when the server says so. */
    inviteCode: z.string().trim().max(64).optional(),
    /** 0034: an accounting firm (many clients) or a finance team (its own company). */
    kind: z.enum(["FIRM", "FINANCE_TEAM"]).default("FIRM"),
});
export const LoginRequest = z.object({ email: Email, password: Password });
export const Principal = z.object({
    userId: Uuid,
    email: Email,
    displayName: z.string(),
    practiceId: Uuid,
    firmName: z.string(),
    /** 0034: what the workspace is — a firm with clients, or a finance team with its own company. */
    firmKind: z.enum(["FIRM", "FINANCE_TEAM"]),
    role: MemberRole,
});
export const MeResponse = z.object({ principal: Principal });
export const SessionResponse = z.object({ principal: Principal, csrfToken: z.string() });
// ── the team (ADR-0021 §9, 0034) ─────────────────────────────────────────────
export const TeamMember = z.object({
    memberId: Uuid, userId: Uuid, displayName: z.string(), email: z.string(), role: MemberRole,
    joinedAt: z.string(), joinedAtText: z.string(),
    review: z.enum(["FIRM", "ENGAGEMENTS"]).nullable(), reviewGrantId: Uuid.nullable(), grantsAuthority: z.boolean(), you: z.boolean(),
});
export const TeamInvitation = z.object({
    id: Uuid, email: z.string(), role: MemberRole, invitedByName: z.string(),
    invitedAt: z.string(), invitedAtText: z.string(), expiresAt: z.string(), expiresAtText: z.string(), state: z.enum(["OPEN", "EXPIRED"]),
});
export const TeamResponse = z.object({ members: z.array(TeamMember), invitations: z.array(TeamInvitation), viewerMayManage: z.boolean() });
export const InviteMemberRequest = z.object({ email: Email, role: MemberRole });
/** The link's token, shown ONCE: the browser builds `/join#<token>` from it. */
export const InviteMemberResponse = z.object({ team: TeamResponse, token: z.string(), /** The link was emailed to the colleague (a mail provider is set). */ emailed: z.boolean().default(false) });
export const RemoveMemberRequest = z.object({ reason: z.string().trim().min(1, "State the reason").max(2000) });
export const AssignRequest = z.object({ objectKind: z.enum(["ENGAGEMENT", "WORK"]), objectId: Uuid, assigneeMemberId: Uuid.nullable() });
export const Assignment = z.object({
    memberId: Uuid, userId: Uuid, displayName: z.string(), assignedByName: z.string(), assignedAt: z.string(), assignedAtText: z.string(),
});
export const AssignmentResponse = z.object({ assignment: Assignment.nullable() });
export const PublicInvitationRequest = z.object({ token: z.string().max(64) });
export const PublicInvitationResponse = z.object({
    invitation: z.object({
        firmName: z.string(), firmKind: z.enum(["FIRM", "FINANCE_TEAM"]), email: z.string(), role: MemberRole,
        invitedByName: z.string(), expiresAt: z.string(), expiresAtText: z.string(),
    }),
});
export const AcceptInvitationRequest = z.object({
    token: z.string().max(64), displayName: z.string().trim().min(1).max(200), password: Password,
});
// ── forgot your password? A link by email (0041) ──────────────────────────────
/** The asker always gets the same answer, whether or not an account uses the email. */
export const PasswordResetRequest = z.object({ email: Email });
export const PasswordResetRequestResponse = z.object({ sent: z.literal(true) });
export const PasswordResetCheckRequest = z.object({ token: z.string().max(64) });
export const PasswordResetCheckResponse = z.object({ email: z.string() });
export const PasswordResetUseRequest = z.object({ token: z.string().max(64), password: Password });
// ── the chat box (ADR-0021 §9 part 2): who and when, read from the words ─────
const ChatPeriod = z.object({ from: IsoDate, to: IsoDate, label: z.string(), span: z.enum(["MONTH", "QUARTER", "YEAR"]) });
export const ChatUnderstandRequest = z.object({
    instruction: z.string().trim().max(4000),
    /** Where the box is: a client's page or an engagement's page scopes it. */
    clientId: Uuid.optional(),
    engagementId: Uuid.optional(),
    /** Inside a piece of work (0042): the box knows the job, its client and its period, and asks about none of them. */
    workId: Uuid.optional(),
    /** A client that does not exist yet, named in the conversation: no client question, and its currency is asked. */
    newClientName: z.string().trim().min(2).max(200).optional(),
    /** The kind of work, once the person has said it: no work question. */
    capability: z.string().trim().min(1).max(80).optional(),
    /** Read as the person types: Leveri's own rules only, never a model and never recorded. */
    preview: z.boolean().optional(),
    /** How many files the person is sending with the words: files mean work, whatever the words. */
    files: z.number().int().min(0).max(50).optional(),
    /** One message, however many times it is re-read as its questions are answered: the learning record's key (0037). */
    turnKey: Uuid.optional(),
});
/** One question Leveri must ask before the work can open, with one-tap answers. */
export const ChatQuestion = z.object({
    key: z.enum(["work", "client", "period", "currency"]),
    question: z.string(),
    options: z.array(z.object({ value: z.string(), label: z.string(), detail: z.string().nullable() })),
    /** The person may also type an answer (a new client's name, another currency). */
    freeText: z.string().nullable(),
});
export const ChatUnderstanding = z.object({
    workKind: z.object({ key: z.string(), label: z.string() }).nullable(),
    client: z.object({ id: Uuid, name: z.string() }).nullable(),
    /** A client named in the words who is not one of the firm's yet: created when the work opens. */
    newClientName: z.string().nullable().default(null),
    period: ChatPeriod.nullable(),
    engagement: z.object({ id: Uuid, title: z.string() }).nullable(),
    currency: z.string().nullable(),
    /** What Leveri understood, in one line: "Prepare accounts · Northwind Trading · March 2026". */
    summary: z.string(),
    questions: z.array(ChatQuestion),
    /** Work to do, a question about the firm's work, or something Leveri does not do. */
    intent: z.enum(["WORK", "QUESTION", "OUT_OF_SCOPE"]).default("WORK"),
    /** What Leveri says instead of opening work (a decline, or an answer's lead) — always Leveri's own words. */
    reply: z.string().nullable().default(null),
    /** A question's answer, from the firm's own records: the work it is about. */
    answer: z.array(z.object({ workId: Uuid, title: z.string(), detail: z.string() })).default([]),
    /** "What's the update on this job?": the one job, and its update line by line, read from its own file. */
    update: z.object({ workId: Uuid, title: z.string(), detail: z.string(), lines: z.array(z.string()) }).nullable().default(null),
    /** Who read the words: the configured model, or null when Leveri's own rules did. */
    reader: z.object({ provider: z.string(), model: z.string() }).nullable().default(null),
});
export const ChatUnderstandResponse = z.object({ understanding: ChatUnderstanding });
export const ChatOpenRequest = z.object({
    instruction: z.string().trim().min(1).max(4000),
    clientId: Uuid.optional(),
    /** A client that does not exist yet, named in the conversation. */
    newClientName: z.string().trim().min(2).max(200).optional(),
    engagementId: Uuid.optional(),
    period: ChatPeriod.optional(),
    currency: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/).optional(),
    capability: z.string().trim().regex(/^[a-z][a-z0-9_]{2,63}$/).optional(),
    /** The message this work was opened from (0037). */
    turnKey: Uuid.optional(),
});
/** "Not right?" on a Leveri reply: the person's own verdict, in a reviewer's four classes (0037). */
export const ChatFeedbackRequest = z.object({
    correctionClass: z.enum(["WRONG", "MISSING", "NOISE", "WORDING"]),
    note: z.string().trim().max(1000).optional(),
});
export const ChatFeedbackResponse = z.object({ recorded: z.boolean(), sentence: z.string() });
/** The gap log: what the firm asked that no skill does yet (0037). */
export const ChatGapsResponse = z.object({
    gaps: z.array(z.object({ atText: z.string(), instruction: z.string(), reply: z.string().nullable(), askedBy: z.string() })),
});
/**
 * ONE PERSON'S OWN RECENT CONVERSATION in one place (0042): their messages and Leveri's replies, oldest first.
 * Only the caller's own; a colleague never sees them. The work a message opened is linked, because the work is shared.
 */
export const ChatHistoryResponse = z.object({
    turns: z.array(z.object({
        turnKey: Uuid, atText: z.string(), instruction: z.string(), reply: z.string().nullable(),
        openedWork: z.object({ id: Uuid, title: z.string() }).nullable(),
    })),
});
/** Which place's conversation: none of these is Home; a malformed id is refused, never read as Home. */
export const ChatHistoryQuery = z.object({ clientId: Uuid.optional(), engagementId: Uuid.optional(), workId: Uuid.optional() });
// ── firm ────────────────────────────────────────────────────────────────────
export const ClientStatus = z.enum(["ACTIVE", "ARCHIVED"]);
export const Client = z.object({
    id: Uuid,
    name: z.string(),
    status: ClientStatus,
    createdAt: z.string(),
    /** The same instant, read for a person. Formatted here, never in a browser. */
    createdAtText: z.string(),
});
export const CreateClientRequest = z.object({ name: z.string().trim().min(1).max(200) });
export const ClientListResponse = z.object({ clients: z.array(Client) });
export const ClientResponse = z.object({ client: Client });
export const EngagementType = z.enum(["ANNUAL_ACCOUNTS", "MONTHLY_CLOSE", "QUARTERLY_REVIEW", "AD_HOC"]);
export const EngagementStatus = z.enum(["OPEN", "CLOSED"]);
export const Engagement = z.object({
    id: Uuid,
    clientId: Uuid,
    title: z.string(),
    engagementType: EngagementType,
    periodStart: IsoDate,
    cutOff: IsoDate,
    /** R8.1 — the engagement's currency (ISO-4217). Every figure of its work is denominated in it. */
    currency: z.string().regex(/^[A-Z]{3}$/),
    status: EngagementStatus,
    createdAt: z.string(),
    /** The whole period as one phrase: "1 January 2026 to 31 August 2026". */
    periodText: z.string(),
});
/**
 * OPENING AN ENGAGEMENT asks for what the WORK needs, not for what the schema
 * holds.
 *
 * The period is genuinely required: the capability reconciles as at a cut-off,
 * and every figure it establishes is denominated by that period. It is not
 * asked for because a column exists.
 *
 * The TYPE is not. Nothing in the professional core reads it — no capability,
 * no plan, no conclusion — so it is optional here and defaults to a one-off
 * piece of work. It stays in the contract and in the record because a firm
 * that wants to file its engagements by kind should be able to; it is simply
 * not a question a person must answer before they can begin.
 */
export const CreateEngagementRequest = z.object({
    title: z.string().trim().min(1).max(200),
    engagementType: EngagementType.optional(),
    periodStart: IsoDate,
    cutOff: IsoDate,
    /**
     * R8.1 — REQUIRED, like the period: every figure the work establishes is
     * denominated in it, and the product no longer assumes one for anybody.
     */
    currency: z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, "Use the three-letter currency code, such as USD, GBP or EUR"),
}).refine((e) => e.periodStart <= e.cutOff, { message: "The cut-off cannot be before the period start", path: ["cutOff"] });
export const EngagementListResponse = z.object({ engagements: z.array(Engagement) });
export const EngagementResponse = z.object({ engagement: Engagement });
/** An engagement AND the client it is for: one read for one professional context. */
export const EngagementContextResponse = z.object({
    engagement: Engagement, client: Client,
    /** 0034: the member who holds this engagement, when someone does. */
    assignee: z.object({ memberId: Uuid, userId: Uuid, displayName: z.string() }).nullable().default(null),
});
/**
 * R8.1 — MATERIALITY, set by a person with review authority on the
 * engagement. Amounts are entered as decimal text in the engagement's
 * currency ("100000.00"); the server converts them, the browser never does.
 */
const DecimalAmount = z.string().trim().regex(/^\d{1,15}(\.\d{1,2})?$/, "Enter an amount such as 100000.00");
export const SetMaterialityRequest = z.object({
    overall: DecimalAmount,
    performance: DecimalAmount,
    clearlyTrivial: DecimalAmount,
    basis: z.string().trim().min(1, "State the basis for this materiality").max(2000),
});
export const Materiality = z.object({
    overallMinor: z.number().int().positive(),
    performanceMinor: z.number().int().positive(),
    clearlyTrivialMinor: z.number().int().positive(),
    overallText: z.string(),
    performanceText: z.string(),
    clearlyTrivialText: z.string(),
    basis: z.string(),
    setByName: z.string(),
    setAt: z.string(),
});
export const MaterialityResponse = z.object({
    materiality: Materiality.nullable(),
    /** Whether THIS viewer holds the review authority that setting materiality requires. Decided on the server. */
    viewerMaySet: z.boolean().default(false),
});
/**
 * ADR-0021 §5b — THE ENGAGEMENT FILE: what is known about the client and the
 * engagement before any transaction is worked, layer by layer. Every word is
 * the server's; the browser renders the entries and sends answers.
 */
const FileOption = z.object({ value: z.string(), label: z.string() });
export const FileValueShape = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("CHOICE"), options: z.array(FileOption) }),
    z.object({ kind: z.literal("CHOICES"), options: z.array(FileOption), exclusive: z.string().optional() }),
    z.object({ kind: z.literal("YES_NO") }),
    z.object({ kind: z.literal("TEXT"), max: z.number().int().positive() }),
    z.object({ kind: z.literal("AMOUNT") }),
]);
export const FileEntry = z.object({
    key: z.string(),
    label: z.string(),
    status: z.enum(["ESTABLISHED", "ASSUMED", "NOT_APPLICABLE", "UNKNOWN"]),
    origin: z.enum(["RECORDED", "DERIVED"]),
    valueText: z.string().nullable(),
    clause: z.string().nullable(),
    items: z.array(z.string()),
    statedBy: z.enum(["CLIENT", "ACCOUNTANT"]).nullable(),
    basis: z.string().nullable(),
    recordedByName: z.string().nullable(),
    recordedAt: z.string().nullable(),
    recordedInTitle: z.string().nullable(),
    why: z.string(),
    feeds: z.string().nullable(),
    question: z.string().nullable(),
    answeredBy: z.enum(["CLIENT", "ACCOUNTANT"]),
    needed: z.boolean(),
    policy: z.boolean(),
    shape: FileValueShape.nullable(),
});
export const EngagementFileResponse = z.object({
    catalogue: z.string(),
    currency: z.string(),
    layers: z.array(z.object({ layer: z.string(), title: z.string(), entries: z.array(FileEntry) })),
    questions: z.array(z.string()),
    neededOpen: z.number().int().nonnegative(),
    summary: z.string(),
    readyForTransactions: z.boolean(),
    /** Whether THIS viewer holds the review authority a policy requires. Decided on the server. */
    viewerMaySetPolicy: z.boolean(),
});
/** One answer. An amount arrives as decimal text in the engagement's currency and is converted on the server. */
export const RecordFileEntryRequest = z.object({
    key: z.string().min(1).max(80),
    status: z.enum(["ESTABLISHED", "ASSUMED", "NOT_APPLICABLE"]),
    value: z.union([z.string().max(500), z.boolean(), z.array(z.string().max(80)).max(20), z.null()]).optional(),
    amount: DecimalAmount.optional(),
    statedBy: z.enum(["CLIENT", "ACCOUNTANT"]).optional(),
    basis: z.string().trim().max(2000).optional(),
});
// ── the Work OS (R5) ────────────────────────────────────────────────────────
export * from "./work.js";
// ── AI intent & professional planning (L3) ──────────────────────────────────
export * from "./intent.js";
export * from "./usage.js";
export * from "./connections.js";
export * from "./interest.js";
//# sourceMappingURL=index.js.map